import { ComponentRef, Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'
import { CdkScrollable, ConnectedPosition, ConnectionPositionPair, Overlay, OverlayPositionBuilder, OverlayRef, ScrollDispatcher } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { PolymorpheusContent } from '@alliance/shared/polymorpheus'
import { BehaviorSubject, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { OverlayTooltipComponent } from './overlay-tooltip/overlay-tooltip.component'
import { TooltipPosition } from './tooltip-position-enum'

@Directive()
export abstract class BaseTooltipDirective implements OnInit, OnDestroy {
  @Input() public tooltipContent: PolymorpheusContent | null | undefined
  @Input() public tooltipContext: unknown
  @Input() public tooltipWrapText: boolean | undefined
  @Input() public tooltipAdditionalOffset: number | undefined
  @Input() public tooltipCustomOffsetClass: string | undefined
  @Input() public tooltipPosition: TooltipPosition = TooltipPosition.bottom
  @Input() public scrollContainers: CdkScrollable[] = []
  @Input() public controlledByParent = false
  @Input() public disableTooltip = false
  @Input() public hideOnScroll = true

  @Output() public openedChanges = new EventEmitter<boolean>()

  protected opened$ = new BehaviorSubject(false)
  protected tooltipMobileBottomOffset = 0

  private overlayRef: OverlayRef | undefined
  private tooltipInstance: OverlayTooltipComponent | undefined

  private positionConfig: Record<TooltipPosition, ConnectedPosition> = {
    [TooltipPosition.bottom]: {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 10
    },
    [TooltipPosition.top]: {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -10
    },
    [TooltipPosition.left]: {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -10
    },
    [TooltipPosition.right]: {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 10
    }
  }

  private onDestroy$ = new Subject<void>()

  public constructor(private overlay: Overlay, private overlayPositionBuilder: OverlayPositionBuilder, protected elementRef: ElementRef, private scrollDispatcher: ScrollDispatcher) {}

  @Input()
  public set opened(opened: boolean) {
    this.opened$.next(opened)
  }

  public ngOnInit(): void {
    this.opened$
      .pipe(
        filter(() => this.controlledByParent),
        takeUntil(this.onDestroy$)
      )
      .subscribe((isOpened: boolean) => (isOpened ? this.showTooltip() : this.hideTooltip()))
  }

  public ngOnDestroy(): void {
    this.destroyOverlay()
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  protected showTooltip(): void {
    this.overlayRef = this.createOverlay()
    const tooltipPortal = new ComponentPortal(OverlayTooltipComponent)
    const tooltipRef: ComponentRef<OverlayTooltipComponent> = this.overlayRef.attach(tooltipPortal)

    this.tooltipInstance = tooltipRef.instance

    this.tooltipInstance.content = this.tooltipContent ?? undefined
    this.tooltipInstance.context = this.tooltipContext
    this.tooltipInstance.wrapText = this.tooltipWrapText
    this.tooltipInstance.tooltipPosition = this.tooltipPosition
    this.tooltipInstance.additionalOffset = this.tooltipAdditionalOffset
    this.tooltipInstance.customOffsetClass = this.tooltipCustomOffsetClass
    this.tooltipInstance.mobileBottomOffset = this.tooltipMobileBottomOffset ?? 0

    tooltipRef.instance.detectChanges()
    this.overlayRef.updatePosition()

    this.openedChanges.emit(true)
  }

  protected hideTooltip(): void {
    this.destroyOverlay()
    this.openedChanges.emit(false)
  }

  protected createOverlay(): OverlayRef {
    const parentScrollList = this.scrollDispatcher.getAncestorScrollContainers(this.elementRef)

    const scrollStrategy = this.hideOnScroll ? this.overlay.scrollStrategies.close({ threshold: 5 }) : this.overlay.scrollStrategies.reposition()

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositionListWithFallback())
      .withScrollableContainers([...parentScrollList, ...this.scrollContainers])

    positionStrategy.positionChanges.pipe(takeUntil(this.onDestroy$)).subscribe(change => {
      this.changeTooltipPositionByConnectionPair(change.connectionPair)

      if (this.tooltipInstance && this.tooltipInstance.tooltipPosition !== this.tooltipPosition) {
        this.tooltipInstance.tooltipPosition = this.tooltipPosition
        this.tooltipInstance.detectChanges()
      }
    })

    return this.overlay.create({ positionStrategy, scrollStrategy })
  }

  protected destroyOverlay(): void {
    this.overlayRef?.detach()
    this.overlayRef?.dispose()
    this.overlayRef = undefined
  }

  private getPositionListWithFallback(): ConnectedPosition[] {
    let fallbackPosition: TooltipPosition | undefined

    switch (this.tooltipPosition) {
      case TooltipPosition.bottom:
        fallbackPosition = TooltipPosition.top
        break
      case TooltipPosition.top:
        fallbackPosition = TooltipPosition.bottom
        break
      case TooltipPosition.left:
        fallbackPosition = TooltipPosition.right
        break
      case TooltipPosition.right:
        fallbackPosition = TooltipPosition.left
        break
      default:
        fallbackPosition = TooltipPosition.top
        break
    }

    return [this.positionConfig[this.tooltipPosition], this.positionConfig[fallbackPosition]]
  }

  private changeTooltipPositionByConnectionPair(connectionPair: ConnectionPositionPair): void {
    const { originX, originY } = connectionPair
    const position = originX !== 'center' ? originX : originY

    switch (position) {
      case 'bottom':
        this.tooltipPosition = TooltipPosition.bottom
        break
      case 'top':
        this.tooltipPosition = TooltipPosition.top
        break
      case 'start':
        this.tooltipPosition = TooltipPosition.left
        break
      case 'end':
        this.tooltipPosition = TooltipPosition.right
        break
    }
  }
}
